import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import moment, { Moment } from 'moment-timezone';
import { Controller, useFormContext } from 'react-hook-form';

import { Dropdown, Input, Select } from 'antd';
import { selectTranslations } from 'app/pages/App/slice/selectors';

import { ReactComponent as ArrowDownIcon } from 'app/assets/img/arrow-down-icon.svg';
import StyledButton from 'app/components/StyledButton';

import { ReactComponent as ExportExcel } from 'app/assets/img/export-excel-icon.svg';
import useWindowSize from 'app/hooks/useWindowSize';
import { StyledStack } from 'app/components/Stack';

import { ReactComponent as FilterIcon } from 'app/assets/img/filter-icon.svg';
import { SmallTitle } from './OverviewScreen';
import { PageStateContext } from '..';
import { CallbackOptions } from 'app/hooks/useFetchData';
import { selectListPaymentMethod } from 'app/pages/Checkout/slice/selectors';
import {
  GET_LIST_PAYMENT_METHOD,
  useCheckoutSlice,
} from 'app/pages/Checkout/slice';
import { exportTransactionCSV } from 'app/services/agentApi';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import { AxiosError } from 'axios';

export enum TYPE_REPORT {
  OVERVIEW = 1,
  DETAIL,
}
export enum MAIN_TABS {
  OVERVIEW = 'overview',
  TRANSACTION_LIST = 'transaction-list',
}
export type FilterFormValues = {
  dateFrom: Moment | null;
  dateTo: Moment | null;
  documentNumber?: string | undefined;
  transactionType?: string | number | undefined;
  paymentMethod?: string | number | undefined;
  productType?: string | number | undefined;
  page: number;
  limit: number;
  typeReport: number;
  tab?: string;
  dateFromList?: Moment | null;
  dateToList?: Moment | null;
};

interface FilterProps {
  disabledExportExcel?: boolean;
  onSearch: (additionalOptions?: {
    payloadData?: any;
    callback?: CallbackOptions<any>;
  }) => Promise<any>;
}

export const transformFilterValues = (rawData: FilterFormValues) => {
  const tabActive = rawData.tab || MAIN_TABS.OVERVIEW;
  let dateFrom = rawData.dateFrom?.format('DD/MM/YYYY');
  let dateTo =
    rawData.dateTo?.format('DD/MM/YYYY') ||
    rawData.dateFrom?.format('DD/MM/YYYY');
  if (tabActive === MAIN_TABS.TRANSACTION_LIST) {
    dateFrom = rawData.dateFromList?.format('DD/MM/YYYY');
    dateTo =
      rawData.dateToList?.format('DD/MM/YYYY') ||
      rawData.dateFromList?.format('DD/MM/YYYY');
  }
  return {
    dateFrom,
    dateTo,
    documentNumber: rawData.documentNumber ?? '',
    transactionType: rawData.transactionType ?? '',
    paymentMethod: rawData.paymentMethod ?? '',
    page: rawData.page,
    limit: rawData.limit,
    typeReport: rawData.typeReport,
    isHoliday: rawData.productType ?? '',
  };
};

export const createDateObjectByTimezone = (date: Moment | undefined | null) => {
  if (!date) return undefined;

  const vietnamMoment = date.clone().tz('Asia/Ho_Chi_Minh');
  return new Date(
    vietnamMoment.year(),
    vietnamMoment.month(),
    vietnamMoment.date(),
    vietnamMoment.hours(),
    vietnamMoment.minutes(),
    vietnamMoment.seconds(),
    vietnamMoment.milliseconds(),
  );
};

export const createMomentByTimezone = (date: Date | undefined | null) => {
  if (!date) return undefined;

  return moment.tz(
    {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
      millisecond: date.getMilliseconds(),
    },
    'Asia/Ho_Chi_Minh',
  );
};

const TRANSACTION_TYPES = [
  { labelKey: 'b2b.text.all', value: null },
  { labelKey: 'b2b.transactionType.buy', value: 'F2,ZBU,DC' },
  { labelKey: 'b2b.transactionType.refund', value: 'ZRE,DE' },
  { labelKey: 'b2b.transactionType.pay', value: 'PAY,BC' },
  { labelKey: 'b2b.transactionType.transferCreditToDeposit', value: 'DK' },
  { labelKey: 'b2b.transactionType.rechargeTradeDiscount', value: 'DD' },
  { labelKey: 'b2b.transactionType.deposit', value: 'DX' },
  { labelKey: 'b2b.txt.refund-amount', value: 'DK1' },
];
const PRODUCT_TYPES = [
  { labelKey: 'b2b.text.all', value: null },
  { labelKey: 'b2b.txt.parkTicket', value: 0 },
  { labelKey: 'b2b.txt.holidayComboTicket', value: 1 },
];

const Filter: React.FC<FilterProps> = props => {
  const { onSearch, disabledExportExcel } = props;

  const translations = useSelector(selectTranslations);
  const { filterRef, setPageLoading } = useContext(PageStateContext);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();

  const { windowSize } = useWindowSize();

  const handleExportExcel = async () => {
    try {
      setPageLoading(true);
      const { page, limit, ...params } = transformFilterValues(
        filterRef.current,
      );

      const res = await exportTransactionCSV(params);

      if (res?.data?.success) {
        window.location.href = res?.data?.data?.path;
      }
    } catch (error: any) {
      onOpenDialogNoti({
        type: 'ERROR',
        content:
          error instanceof AxiosError
            ? error?.response?.data?.message
            : error?.message,
        isShow: true,
        title: '',
        onCloseDialog: onCloseDialogNoti,
      });
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <Wrapper>
      {windowSize.width >= 992 ? (
        <FilterComponent onSearch={onSearch} />
      ) : (
        <MobileFilterComponent onSearch={onSearch} />
      )}

      <Button
        onClick={handleExportExcel}
        disabled={disabledExportExcel}
        style={{
          filter: `grayscale(${disabledExportExcel ? '1' : '0'})`,
          cursor: disabledExportExcel ? 'not-allowed' : 'pointer',
          marginLeft: 'auto',
        }}
      >
        <ExportExcel />
        {windowSize.width >= 1280 && (
          <SmallTitle
            style={{
              margin: 0,
              color: '#fab317',
              fontSize: 14,
              width: 'max-content',
              marginLeft: 6,
              fontWeight: 400,
            }}
          >
            {translations?.['b2b.txt.export-excel']}
          </SmallTitle>
        )}
      </Button>
    </Wrapper>
  );
};

export default Filter;

const FilterComponent: React.FC<{
  isMobile?: boolean;
  onSearch?: FilterProps['onSearch'];
  onBeforeSearch?: () => void;
}> = ({ isMobile, onSearch, onBeforeSearch }) => {
  useCheckoutSlice();
  const translations = useSelector(selectTranslations);
  const { control, handleSubmit, setValue, watch } =
    useFormContext<FilterFormValues>();
  const paymentMethod = watch('paymentMethod');
  const transactionType = watch('transactionType');
  const productType = watch('productType');
  const { filterRef, tableRef, anchorPointerRef } =
    useContext(PageStateContext);
  const listPaymentMethod = useSelector(selectListPaymentMethod);
  const dispatch = useDispatch();

  const transactionOptions = useMemo(
    () =>
      TRANSACTION_TYPES.map(type => ({
        label: translations?.[type.labelKey] ?? type.labelKey,
        value: type.value,
      })),
    [translations],
  );
  const productTypeOptions = useMemo(
    () =>
      PRODUCT_TYPES.map(type => ({
        label: translations?.[type.labelKey] ?? type.labelKey,
        value: type.value,
      })),
    [translations],
  );

  const paymentOptions = useMemo(() => {
    const result: Record<string, string | number | null>[] = [
      { label: translations?.['b2b.text.all'] ?? '', value: null },
    ];

    listPaymentMethod?.forEach(payment =>
      result.push({
        label: payment?.name || payment?.code,
        value: payment?.mapVGS,
      }),
    );

    return result;
  }, [listPaymentMethod, translations]);

  const handleSearch = (data: FilterFormValues) => {
    const newFilter = { ...data, page: 1 };

    filterRef.current = newFilter;
    setValue('page', 1);

    onBeforeSearch?.();

    onSearch?.({
      payloadData: transformFilterValues(newFilter),
      callback: {
        onSuccess: () => {
          if (anchorPointerRef?.current) {
            const top =
              anchorPointerRef.current.getBoundingClientRect().top +
              window.scrollY -
              130;
            window.scrollTo({ top, behavior: 'smooth' });
          }

          tableRef.current?.scrollTo({ top: 0 });
        },
      },
    });
  };

  useEffect(() => {
    const fetchPaymentMethods = () => {
      if (!listPaymentMethod?.length)
        dispatch(
          GET_LIST_PAYMENT_METHOD({
            ticketType: sessionStorage.getItem('ticketType'),
          }),
        );
    };

    fetchPaymentMethods();
  }, [listPaymentMethod, dispatch]);

  return (
    <FilterContainer style={{ flexDirection: isMobile ? 'column' : 'row' }}>
      <FilterItem isMobile={isMobile}>
        {!isMobile && (
          <SmallTitle style={{ color: '#000000' }}>
            {translations?.['b2b.txt.order-id'] ?? ''}
          </SmallTitle>
        )}

        <Controller
          name="documentNumber"
          control={control}
          render={({ field }) => (
            <InputCustom
              placeholder={translations?.['b2b.txt.enterTransactionCode'] ?? ''}
              isMobile={isMobile}
              autoComplete="off"
              {...field}
            />
          )}
        />
      </FilterItem>

      <FilterItem isMobile={isMobile}>
        {!isMobile && (
          <SmallTitle style={{ color: '#000000' }}>
            {translations?.['b2b.txt.transaction-type'] ?? ''}
          </SmallTitle>
        )}
        <Controller
          name="transactionType"
          control={control}
          render={({ field: { value, ...restProps } }) => (
            <SelectCustom
              value={transactionType}
              placeholder={
                translations?.['b2b.txt.selectTransactionType'] ?? ''
              }
              suffixIcon={<ArrowDownIcon />}
              allowClear
              options={transactionOptions}
              isMobile={isMobile}
              dropdownMatchSelectWidth={false}
              {...restProps}
            />
          )}
        />
      </FilterItem>

      <FilterItem isMobile={isMobile}>
        {!isMobile && (
          <SmallTitle style={{ color: '#000000' }}>
            {translations?.['b2b.table.payment'] ?? ''}
          </SmallTitle>
        )}

        <Controller
          name="paymentMethod"
          control={control}
          render={({ field: { value, ...restField } }) => (
            <SelectCustom
              value={paymentMethod}
              placeholder={translations?.['b2b.txt.selectPaymentMethod'] ?? ''}
              suffixIcon={<ArrowDownIcon />}
              allowClear
              options={paymentOptions}
              isMobile={isMobile}
              dropdownMatchSelectWidth={false}
              {...restField}
            />
          )}
        />
      </FilterItem>
      <FilterItem isMobile={isMobile}>
        {!isMobile && (
          <SmallTitle style={{ color: '#000000' }}>
            {translations?.['b2b.txt.productType'] ?? ''}
          </SmallTitle>
        )}

        <Controller
          name="productType"
          control={control}
          render={({ field: { value, ...restField } }) => (
            <SelectCustom
              value={productType}
              placeholder={translations?.['b2b.txt.selectProductType'] ?? ''}
              suffixIcon={<ArrowDownIcon />}
              allowClear
              options={productTypeOptions}
              isMobile={isMobile}
              dropdownMatchSelectWidth={false}
              {...restField}
            />
          )}
        />
      </FilterItem>
      <StyledButton
        textColor="#ffffff"
        style={{
          height: 36,
          width: isMobile ? '100%' : 'initial',
          alignSelf: 'flex-end',
        }}
        onClick={handleSubmit(handleSearch)}
      >
        {translations?.['b2c.txt.search'] ?? ''}
      </StyledButton>
    </FilterContainer>
  );
};

const MobileFilterComponent: React.FC<{
  onSearch: FilterProps['onSearch'];
}> = ({ onSearch }) => {
  const [menuVisible, setMenuVisible] = useState(false);

  return (
    <Dropdown
      visible={menuVisible}
      trigger={['click']}
      onVisibleChange={visible => setMenuVisible(visible)}
      getPopupContainer={trigger => trigger.parentNode as HTMLElement}
      overlayStyle={{ zIndex: 3 }}
      overlay={
        <StyledStack
          style={{
            background: 'white',
            width: 292,
            padding: 16,
            borderRadius: 10,
            boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
          }}
        >
          <FilterComponent
            isMobile
            onSearch={onSearch}
            onBeforeSearch={() => {
              setMenuVisible(false);
            }}
          />
        </StyledStack>
      }
    >
      <StyledButton
        bgColor="#fff"
        style={{
          border: '1px solid #EDEDED',
          borderRadius: 10,
          width: 41,
          height: 41,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        <FilterIcon width={16} height={16} color="#000" />
      </StyledButton>
    </Dropdown>
  );
};

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 10px;
  flex-direction: column;
  justify-content: space-between;
  @media (max-width: 991px) {
    flex-direction: row;
    align-items: center;
  }
`;

const FilterContainer = styled(Wrapper)`
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const FilterItem = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${props => (props.isMobile ? '100%' : 'auto')};
`;

const InputCustom = styled(Input)<{ isMobile?: boolean }>`
  height: 36px;
  width: ${props => (props.isMobile ? '100%' : '206px')};
  border-radius: 6px;
  border: solid 1px #d9d9d9;
`;

const SelectCustom = styled(Select)<{ isMobile?: boolean }>`
  .ant-select-selector {
    width: ${props => (props.isMobile ? '100%' : '206px')} !important;
    height: 36px !important;
    border: solid 1px #d9d9d9 !important;
    border-radius: 6px !important;
    input {
      height: 100% !important;
    }
    .ant-select-selection-placeholder {
      line-height: 36px;
      opacity: 0.6;
    }
    .ant-select-selection-item {
      line-height: 36px;
    }
  }
  .ant-select-clear {
    .anticon-close-circle {
      display: inline-flex;
    }
  }
`;

export const Button = styled.button`
  display: flex;
  align-items: flex-end;
  border: none;
  outline: none;
  background-color: transparent;
`;
